import React, { useState, useEffect } from 'react';
import { useLazyQuery, useMutation } from "@apollo/client";
import _ from "lodash";

import { GET_LAST_SCORE } from "schemas/profile";
import { GlobalState } from "contexts/GlobalState";
import { UPDATE_USER } from "schemas/profile";

const PSYCHO_MAX = 800;
const PSYCHO_MIN = 200;
const MATURITY_MAX = 117;
const MATURITY_MIN = 55;
const ADAPTER_MAX = 800;
const ADAPTER_MIN = 200;

const FIELD_NAMES = ["maturity", "psycho", "adapter"];

export default function useCalc(labels, inputFields = FIELD_NAMES, calculateOnce = true) {
  const [globalUser, setGlobalUser] = GlobalState("user");
  const [showResult, setShowResult] = useState(false);
  const [formFields, setFormFields] = useState({});
  const [triedToValidate, setTriedToValidate] = useState(false);
  const [showRegister, setShowRegister] = useState(false);
  const [updateUser] = useMutation(UPDATE_USER);
  const [getLastScore, { loading: scoresLoading, data: scoresData }] = useLazyQuery(GET_LAST_SCORE, {
    fetchPolicy: "no-cache",
  });

  useEffect(() => {
    if (!scoresLoading && scoresData) {
      if ((checkIfCalcResultIsInRange() && calculateOnce) || calculateOnce == false) {
        const newFormFields = {
          maturity: parseFloat(scoresData?.getLastScore?.body?.bagrut),
          psycho: parseFloat(scoresData?.getLastScore?.body?.psicho),
          adapter: parseFloat(scoresData?.getLastScore?.body?.hatama)
        }
        setFormFields(newFormFields);
        setShowResult(true);
      }
    }
  }, [scoresLoading, scoresData])

  const handleChange = (field, target) => {
    setFormFields((prevState) => ({ ...prevState, [field]: target.value }));
  };
  const validate = () => {
    setTriedToValidate(true);
    const fieldsWithContentNames = getFieldsWithContentNames();
    //check that all fields are in the correct ranges
    //only 2 fields are supposed to be full
    if (fieldsWithContentNames.length !== 2 || !checkIfFieldsAreInRange(fieldsWithContentNames)) {
      focusOnFirstInvalidField();
      return false;
    }
    return true;
  }

  const findCalculatedField = () => {
    if (!formFields.maturity) {
      return "maturity";
    }
    if (!formFields.psycho) {
      return "psycho";
    }
    if (!formFields.adapter) {
      return "adapter";
    }
  }

  const resetCalc = () => {
    setFormFields({});
    setShowResult(false);
    setTriedToValidate(false);
  }

  const checkIfCalcResultIsInRange = () => {
    const calculatedFieldName = findCalculatedField();
    return checkIfSingleCalculatedScoreIsInRange(calculatedFieldName);
  }

  const getFieldsWithContentNames = () => {
    let fieldsWithContent = [];
    for (let field of inputFields) {
      if (formFields[field]) {
        fieldsWithContent.push(field);
      }
    }
    return fieldsWithContent;
  }

  const focusOnFirstInvalidField = () => {
    for (let field of inputFields) {
      if (!checkIfSingleFormFieldIsInRange(field)) {
        const element = document.getElementsByName(field)?.[0];
        element.focus();
        break;
      }
    }
  }

  const checkIfInvalid = (fieldName) => {
    if (fieldName === "maturity") {
      if (!checkIfSingleFormFieldIsInRange("maturity") && triedToValidate) {
        return true;
      }
    }
    if (fieldName === "psycho") {
      if (!checkIfSingleFormFieldIsInRange("psycho") && triedToValidate) {
        return true;
      }
    }
    if (fieldName === "adapter") {
      if (!checkIfSingleFormFieldIsInRange("adapter") && triedToValidate) {
        return true;
      }
    }
  }

  const checkIfSingleFormFieldIsInRange = (fieldName) => {
    if (fieldName === "maturity") {
      return (formFields.maturity && formFields.maturity >= MATURITY_MIN && formFields.maturity <= MATURITY_MAX);
    }
    if (fieldName === "psycho") {
      return (formFields.psycho && formFields.psycho >= PSYCHO_MIN && formFields.psycho <= PSYCHO_MAX);
    }
    if (fieldName === "adapter") {
      return (formFields.adapter && formFields.adapter >= ADAPTER_MIN && formFields.adapter <= ADAPTER_MAX);
    }
  }

  const checkIfSingleCalculatedScoreIsInRange = (fieldName) => {
    if (fieldName === "maturity") {
      const calculatedMaturity = scoresData?.getLastScore?.body?.bagrut;
      return (calculatedMaturity >= MATURITY_MIN && calculatedMaturity <= MATURITY_MAX);
    }
    if (fieldName === "psycho") {
      const calculatedPsycho = scoresData?.getLastScore?.body?.psicho;
      return (calculatedPsycho >= PSYCHO_MIN && calculatedPsycho <= PSYCHO_MAX);
    }
    if (fieldName === "adapter") {
      const calculatedAdapter = scoresData?.getLastScore?.body?.hatama;
      return (calculatedAdapter >= ADAPTER_MIN && calculatedAdapter <= ADAPTER_MAX);
    }
  }

  const checkIfFieldsAreInRange = (fields) => {
    for (let field of fields) {
      if (!checkIfSingleFormFieldIsInRange(field)) {
        return false;
      }
    }
    return true;
  }

  const buildJsonAndCallLastScore = (jsonParams) => {
    let scoresData = {
      prog: REACT_APP_MAMTA_SCORE_PROG_VALUE,
      out: "json"
    }
    scoresData.reali10 = 0;
    if (jsonParams.psycho) {
      scoresData.psicho = jsonParams.psycho
    }
    if (jsonParams.maturity) {
      scoresData.bagrut = jsonParams.maturity
    }
    if (jsonParams.adapter) {
      scoresData.hatama = jsonParams.adapter
    }
    if (jsonParams.chug) {
      scoresData.chug = jsonParams.chug
    }
    if (jsonParams.reali10) {
      scoresData.reali10 = jsonParams.reali10;
    }
    getLastScore({
      variables: {
        scoresData: scoresData
      }
    })
  }

  const handleSaveToPersonal = () => {
    if (globalUser.user && scoresData) {
      updateUser({
        variables: {
          psychometric: scoresData?.getLastScore?.body?.psicho ?? globalUser.user.psychometric ?? "",
          maturity: scoresData?.getLastScore?.body?.bagrut ?? globalUser.user.maturity ?? "",
          adapter: scoresData?.getLastScore?.body?.hatama ?? "",
          filters: globalUser.user.filters ?? {},
          registrationFormChoices: globalUser.registrationFormChoices ?? {},
        },
      });
      const userCopy = _.cloneDeep(globalUser);
      userCopy.user.adapter = scoresData?.getLastScore?.body?.hatama;
      setGlobalUser(userCopy);
      window.location.assign(labels.profile_url);
    } else {
      setShowRegister(true);
    }
  }

  return {
    buildJsonAndCallLastScore,
    scoresData,
    formFields,
    handleChange,
    validate,
    checkIfInvalid,
    showResult,
    setShowResult,
    resetCalc,
    handleSaveToPersonal,
    showRegister,
    setShowRegister,
    getFieldsWithContentNames,
    scoresLoading
  }
}